import { NotificationType } from '../enums';
import type {
  NotificationCategoryKey,
  NotificationCategoryPrefs,
  NotificationDeliveryPreset,
  NotificationDigestPrefs,
  NotificationEmailMode,
  NotificationPrefsV2,
} from './notification-channels';

/** Legacy Phase 1 in-app toggles (still supported for migration). */
export type NotificationPrefsLegacy = {
  likes: boolean;
  comments: boolean;
  follows: boolean;
  creatorPosts: boolean;
  mentions: boolean;
  directMessages: boolean;
  pushDirectMessages: boolean;
};

export const DEFAULT_NOTIFICATION_PREFS_LEGACY: NotificationPrefsLegacy = {
  likes: true,
  comments: true,
  follows: true,
  creatorPosts: true,
  mentions: true,
  directMessages: true,
  pushDirectMessages: false,
};

const RECOMMENDED_CATEGORIES: Record<NotificationCategoryKey, NotificationCategoryPrefs> = {
  messages: { inApp: true, email: 'missed_only', push: false },
  mentions: { inApp: true, email: 'missed_only', push: false },
  comments: { inApp: true, email: 'daily_digest', push: false },
  social: {
    inApp: true,
    email: 'off',
    push: false,
    granular: { likes: true, follows: true, reposts: true, saves: true },
  },
  creators: { inApp: true, email: 'daily_digest', push: false },
  communities: { inApp: true, email: 'missed_only', push: false },
  platform: { inApp: false, email: 'off', push: false },
  account: { inApp: true, email: 'immediate', push: false },
};

export const DEFAULT_DIGEST_PREFS: NotificationDigestPrefs = {
  enabled: true,
  frequency: 'daily',
  hourUtc: 8,
};

export function buildNotificationPrefsV2(
  preset: NotificationDeliveryPreset = 'recommended',
): NotificationPrefsV2 {
  const categories = { ...RECOMMENDED_CATEGORIES };

  if (preset === 'in_app_only') {
    for (const key of Object.keys(categories) as NotificationCategoryKey[]) {
      categories[key] = { ...categories[key], email: 'off' };
    }
  }

  if (preset === 'all_email') {
    categories.messages.email = 'missed_only';
    categories.mentions.email = 'missed_only';
    categories.comments.email = 'daily_digest';
    categories.social.email = 'off';
    categories.creators.email = 'daily_digest';
    categories.communities.email = 'missed_only';
    categories.platform.email = 'off';
    categories.account.email = 'immediate';
  }

  return {
    version: 2,
    preset,
    digest: { ...DEFAULT_DIGEST_PREFS },
    categories,
  };
}

export const DEFAULT_NOTIFICATION_PREFS_V2 = buildNotificationPrefsV2('recommended');

export function categoryForNotificationType(type: NotificationType): NotificationCategoryKey {
  switch (type) {
    case NotificationType.DIRECT_MESSAGE:
      return 'messages';
    case NotificationType.MENTION:
      return 'mentions';
    case NotificationType.POST_COMMENTED:
      return 'comments';
    case NotificationType.POST_LIKED:
    case NotificationType.NEW_FOLLOWER:
    case NotificationType.POST_REPOSTED:
    case NotificationType.POST_SAVED:
      return 'social';
    case NotificationType.CREATOR_POST:
      return 'creators';
    case NotificationType.CREATOR_PROFILE_COMPLETED:
      return 'creators';
    case NotificationType.COMMUNITY_JOIN_WELCOME:
    case NotificationType.COMMUNITY_REPLY:
    case NotificationType.COMMUNITY_MENTION:
    case NotificationType.COMMUNITY_PIN:
      return 'communities';
    case NotificationType.COURSE_DRIP_UNLOCK:
    case NotificationType.COURSE_CERTIFICATE_READY:
    case NotificationType.COURSE_NEW_LESSON:
    case NotificationType.COURSE_QA_REPLY:
    case NotificationType.COURSE_ENROLLED:
    case NotificationType.COURSE_PURCHASE_RECEIPT:
      return 'creators';
    case NotificationType.MODERATION_ACTION:
    case NotificationType.SUBSCRIPTION_UPDATED:
    case NotificationType.COMMUNITY_MOD_ACTION:
    case NotificationType.COMMUNITY_BILLING:
    case NotificationType.EVENT_REMINDER:
      return 'account';
    default:
      return 'platform';
  }
}

export function legacyKeyForNotificationType(type: NotificationType): keyof NotificationPrefsLegacy | null {
  switch (type) {
    case NotificationType.POST_LIKED:
      return 'likes';
    case NotificationType.POST_COMMENTED:
      return 'comments';
    case NotificationType.NEW_FOLLOWER:
      return 'follows';
    case NotificationType.CREATOR_POST:
      return 'creatorPosts';
    case NotificationType.MENTION:
      return 'mentions';
    case NotificationType.DIRECT_MESSAGE:
      return 'directMessages';
    default:
      return null;
  }
}

export function parseNotificationPrefs(raw: unknown): NotificationPrefsV2 {
  if (!raw || typeof raw !== 'object') return DEFAULT_NOTIFICATION_PREFS_V2;
  const p = raw as Record<string, unknown>;

  if (p.version === 2) {
    const digest = (p.digest ?? {}) as Partial<NotificationDigestPrefs>;
    const categoriesRaw = (p.categories ?? {}) as Partial<
      Record<NotificationCategoryKey, Partial<NotificationCategoryPrefs>>
    >;
    const base = buildNotificationPrefsV2(
      (p.preset as NotificationDeliveryPreset) ?? 'recommended',
    );

    return {
      version: 2,
      preset: (p.preset as NotificationDeliveryPreset) ?? base.preset,
      digest: {
        enabled: digest.enabled !== false,
        frequency: digest.frequency === 'weekly' ? 'weekly' : 'daily',
        hourUtc: typeof digest.hourUtc === 'number' ? digest.hourUtc : DEFAULT_DIGEST_PREFS.hourUtc,
      },
      categories: (Object.keys(base.categories) as NotificationCategoryKey[]).reduce(
        (acc, key) => {
          const incoming = categoriesRaw[key];
          acc[key] = {
            inApp: incoming?.inApp !== false && base.categories[key].inApp,
            email: (incoming?.email as NotificationEmailMode) ?? base.categories[key].email,
            push: incoming?.push ?? base.categories[key].push,
            ...(key === 'social' && incoming?.granular
              ? { granular: { ...base.categories.social.granular, ...incoming.granular } }
              : key === 'social' && base.categories.social.granular
                ? { granular: base.categories.social.granular }
                : {}),
          };
          return acc;
        },
        {} as Record<NotificationCategoryKey, NotificationCategoryPrefs>,
      ),
    };
  }

  const legacy = parseNotificationPrefsLegacy(raw);
  const migrated = buildNotificationPrefsV2('recommended');
  migrated.categories.social.inApp = legacy.likes || legacy.follows;
  migrated.categories.social.granular = {
    likes: legacy.likes,
    follows: legacy.follows,
    reposts: legacy.likes,
    saves: legacy.likes,
  };
  migrated.categories.comments.inApp = legacy.comments;
  migrated.categories.creators.inApp = legacy.creatorPosts;
  migrated.categories.mentions.inApp = legacy.mentions;
  migrated.categories.messages.inApp = legacy.directMessages;
  migrated.categories.messages.push = legacy.pushDirectMessages;
  migrated.preset = 'custom';
  return migrated;
}

export function parseNotificationPrefsLegacy(raw: unknown): NotificationPrefsLegacy {
  if (!raw || typeof raw !== 'object') return DEFAULT_NOTIFICATION_PREFS_LEGACY;
  const p = raw as Record<string, unknown>;
  return {
    likes: p.likes !== false,
    comments: p.comments !== false,
    follows: p.follows !== false,
    creatorPosts: p.creatorPosts !== false,
    mentions: p.mentions !== false,
    directMessages: p.directMessages !== false,
    pushDirectMessages: p.pushDirectMessages === true,
  };
}

export function isInAppEnabled(prefs: NotificationPrefsV2, type: NotificationType): boolean {
  const category = categoryForNotificationType(type);
  const cat = prefs.categories[category];
  if (cat?.inApp === false) return false;

  if (category === 'social') {
    const granular = cat.granular ?? {};
    switch (type) {
      case NotificationType.POST_LIKED:
        return granular.likes !== false;
      case NotificationType.NEW_FOLLOWER:
        return granular.follows !== false;
      case NotificationType.POST_REPOSTED:
        return granular.reposts !== false;
      case NotificationType.POST_SAVED:
        return granular.saves !== false;
      default:
        return true;
    }
  }

  return true;
}

export function isPushEnabled(prefs: NotificationPrefsV2, type: NotificationType): boolean {
  const category = categoryForNotificationType(type);
  const cat = prefs.categories[category];
  if (cat?.push !== true) return false;

  if (category === 'social') {
    const granular = cat.granular ?? {};
    switch (type) {
      case NotificationType.POST_LIKED:
        return granular.likesPush === true && granular.likes !== false;
      case NotificationType.NEW_FOLLOWER:
        return granular.followsPush === true && granular.follows !== false;
      case NotificationType.POST_REPOSTED:
        return granular.repostsPush === true && granular.reposts !== false;
      case NotificationType.POST_SAVED:
        return granular.savesPush === true && granular.saves !== false;
      default:
        return true;
    }
  }

  return true;
}

export function emailModeForType(prefs: NotificationPrefsV2, type: NotificationType): NotificationEmailMode {
  const category = categoryForNotificationType(type);
  return prefs.categories[category]?.email ?? 'off';
}

/** True when email mode is immediate or missed (not digest-only). */
export function isDirectEmailEnabled(prefs: NotificationPrefsV2, type: NotificationType): boolean {
  const mode = emailModeForType(prefs, type);
  return mode === 'immediate' || mode === 'missed_only';
}

/** True when email digest mode applies and digest is enabled. */
export function isDigestEmailEnabled(prefs: NotificationPrefsV2, type: NotificationType): boolean {
  if (!prefs.digest.enabled) return false;
  const mode = emailModeForType(prefs, type);
  if (mode === 'daily_digest' && prefs.digest.frequency === 'daily') return true;
  if (mode === 'weekly_digest' && prefs.digest.frequency === 'weekly') return true;
  return false;
}

/** Any delivery channel (in-app, direct email, digest, or push) is enabled. */
export function isAnyNotificationChannelEnabled(
  prefs: NotificationPrefsV2,
  type: NotificationType,
): boolean {
  if (isInAppEnabled(prefs, type)) return true;
  if (isPushEnabled(prefs, type)) return true;
  if (isDirectEmailEnabled(prefs, type)) return true;
  if (isDigestEmailEnabled(prefs, type)) return true;
  return false;
}

export const NOTIFICATION_CHANNELS_PAYLOAD_KEY = '_channels';

export type NotificationChannelsMeta = {
  inApp: boolean;
};

export function withNotificationChannels(
  payload: Record<string, unknown>,
  channels: NotificationChannelsMeta,
): Record<string, unknown> {
  return { ...payload, [NOTIFICATION_CHANNELS_PAYLOAD_KEY]: channels };
}

export function notificationChannelsFromPayload(
  payload: Record<string, unknown>,
): NotificationChannelsMeta {
  const raw = payload[NOTIFICATION_CHANNELS_PAYLOAD_KEY];
  if (raw && typeof raw === 'object' && 'inApp' in (raw as object)) {
    return { inApp: (raw as NotificationChannelsMeta).inApp !== false };
  }
  return { inApp: true };
}
